import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  User as UserIcon, 
  Trophy, 
  Zap, 
  Target, 
  Calendar, 
  TrendingUp,
  Settings,
  Crown,
  Award,
  BarChart3,
  Activity,
  Edit,
  PenTool,
  ExternalLink,
  Lock,
  ChevronRight,
  Save,
  Trash2,
  Flag,
  Gift,
  Users,
  Timer,
  BookOpen,
  Medal,
  ArrowLeft,
  CheckCircle2,
  Code2,
  Flame,
  Gem,
  Star,
  Shield,
  Rocket,
  Lightbulb,
  BadgeCheck,
  Sparkles,
  Cpu,
  Keyboard,
  ChevronsUp,
  CircleDashed,
  Swords,
  Clock,
  CheckCheck
} from 'lucide-react';
import { useUser } from '../context/UserContext';
import { useAuth } from '../context/AuthContext';
import { userAPI, typingTestAPI } from '../utils/apiClient';
import { User, UserActivity } from '../types';
import toast from 'react-hot-toast';
import CountrySelector from '../components/CountrySelector';
import FlagIcon from '../components/FlagIcon';
import { GLOBAL_IDENTIFIER } from '../utils/countryFlags';
import { useFlagCache } from '../hooks/useFlagCache';
import Auth from './Auth';

const Profile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const { state: userState, updateProfile, updatePreferences, logout } = useUser();
  const { isAuthenticated, user: authUser, logout: authLogout } = useAuth();
  const { preloadCountryFlags } = useFlagCache();
  const [activeTab, setActiveTab] = useState<'overview' | 'public-profile' | 'stats' | 'achievements' | 'settings'>('overview');
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  // Determine if viewing own profile or someone else's
  const isOwnProfile = !userId || (userState.user?.id === userId);
  const [otherUserData, setOtherUserData] = useState<User | null>(null);
  const [isLoadingUser, setIsLoadingUser] = useState(false);
  const [recentActivity, setRecentActivity] = useState<UserActivity[]>([]);
  const [loadingActivity, setLoadingActivity] = useState(false);
  
  // Fetch other user data if viewing someone else's profile
  useEffect(() => {
    if (!isOwnProfile && userId) {
      setIsLoadingUser(true);
      
      // Fetch user data from API
      userAPI.getById(userId)
        .then((fetchedUser) => {
          setOtherUserData(fetchedUser);
          
          // Preload the flag for this user
          if (fetchedUser.country) {
            preloadCountryFlags([fetchedUser.country]);
          }
          
          // If viewing someone else's profile, default to the public profile tab
          if (!isOwnProfile) {
            setActiveTab('public-profile');
          }
        })
        .catch((error) => {
          console.error('Error fetching user:', error);
          toast.error('Failed to load user profile');
          setOtherUserData(null);
        })
        .finally(() => {
          setIsLoadingUser(false);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isOwnProfile]);

  // Fetch recent activity for the profile being viewed
  useEffect(() => {
    const profileUserId = isOwnProfile ? userState.user?.id : userId;
    
    if (profileUserId) {
      setLoadingActivity(true);
      typingTestAPI.getRecentActivity(profileUserId, 5)
        .then((activity) => {
          setRecentActivity(activity);
        })
        .catch((error) => {
          console.error('Error fetching recent activity:', error);
          setRecentActivity([]);
        })
        .finally(() => {
          setLoadingActivity(false);
        });
    }
  }, [isOwnProfile, userState.user?.id, userId]);

  // Check if there's an activeTab in the location state (from redirects)
  useEffect(() => {
    if (location.state?.activeTab) {
      setActiveTab(location.state.activeTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);
  const [editingProfile, setEditingProfile] = useState(false);
  const [formData, setFormData] = useState({
    username: userState.user?.username || authUser?.name || '',
    email: userState.user?.email || authUser?.email || '',
    bio: '',
    github: '',
    twitter: '',
    country: userState.user?.country || GLOBAL_IDENTIFIER,
  });

  const handleEditToggle = () => {
    if (editingProfile) {
      // Save profile changes
      updateProfile({
        username: formData.username,
        email: formData.email,
        bio: formData.bio,
        country: formData.country,
      });
      toast.success('Profile updated successfully!');
    }
    setEditingProfile(!editingProfile);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!userState.isAuthenticated) {
    return (
      <>
        <motion.div 
          className="max-w-4xl mx-auto text-center py-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <UserIcon className="w-16 h-16 mx-auto mb-4 text-textMuted" />
          <h1 className="text-2xl font-bold mb-2">Please Log In</h1>
          <p className="text-textMuted mb-6">You need to be logged in to view your profile.</p>
          <button 
            onClick={() => setIsAuthModalOpen(true)}
            className="bg-accent hover:bg-yellow-500 text-primary font-medium px-6 py-2 rounded-lg transition-colors"
          >
            Login
          </button>
        </motion.div>
        <Auth isOpen={isAuthModalOpen} onClose={() => setIsAuthModalOpen(false)} />
      </>
    );
  }

  // Use userData from the userState to render the profile information
  // userState.user has the typing statistics and preferences
  // authUser has the basic user info like name and email
  const userData = !isOwnProfile && otherUserData 
    ? otherUserData 
    : userState.user || {
        username: authUser?.name || 'User',
        email: authUser?.email || '',
        level: 1,
        totalXp: 0,
        createdAt: new Date().toISOString(),
        isPremium: false,
        country: GLOBAL_IDENTIFIER,
        stats: {
          avgWpm: 0,
          bestWpm: 0,
          avgAccuracy: 0,
          bestAccuracy: 0,
          totalTests: 0,
          totalTimeTyping: 0,
          battlesWon: 0,
          battlesLost: 0,
          streak: 0,
          bestStreak: 0
        },
        preferences: {
          theme: 'default',
          fontSize: 16,
          smoothCaret: true,
          soundEnabled: true,
          blindMode: false,
          keymap: 'qwerty'
        },
        recentActivity: []
      };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: UserIcon },
    { id: 'public-profile', label: 'Public Profile', icon: Users },
    { id: 'stats', label: 'Statistics', icon: BarChart3 },
    { id: 'achievements', label: 'Achievements', icon: Award },
    { id: 'settings', label: 'Settings', icon: Settings },
  ] as const;

  const calculateXpToNextLevel = () => {
    const nextLevelXp = userData.level * 1000;
    const currentLevelXp = (userData.level - 1) * 1000;
    const progressXp = userData.totalXp - currentLevelXp;
    const requiredXp = nextLevelXp - currentLevelXp;
    return { progressXp, requiredXp, percentage: (progressXp / requiredXp) * 100 };
  };

  const xpProgress = calculateXpToNextLevel();

  const OverviewTab = () => ( // NOSONAR
    <div className="space-y-6">
      {/* Profile Header */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="flex flex-col md:flex-row md:items-center md:space-x-6">
          <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mb-4 md:mb-0 mx-auto md:mx-0">
            <UserIcon className="w-10 h-10 text-accent" />
          </div>
          <div className="flex-1">
            <div className="flex items-center space-x-3 justify-center md:justify-start">
              <h1 className="text-2xl font-bold">{editingProfile ? formData.username : userData.username}</h1>
              {userData.isPremium && <Crown className="w-6 h-6 text-yellow-500" />}
              {!editingProfile && (
                <button 
                  onClick={handleEditToggle} 
                  className="p-1 text-textMuted hover:text-accent transition-colors"
                  title="Edit Profile"
                >
                  <Edit className="w-4 h-4" />
                </button>
              )}
            </div>
            <p className="text-textMuted text-center md:text-left">{editingProfile ? formData.email : userData.email}</p>
            
            {editingProfile ? (
              <div className="mt-3 space-y-3">
                <div>
                  <label htmlFor="username" className="block text-sm font-medium mb-1">Username</label>
                  <input
                    id="username"
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full bg-secondary border border-border/20 rounded-lg px-3 py-2 text-text focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-1">Email</label>
                  <input
                    id="email"
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full bg-secondary border border-border/20 rounded-lg px-3 py-2 text-text focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="bio" className="block text-sm font-medium mb-1">Bio</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full bg-secondary border border-border/20 rounded-lg px-3 py-2 text-text focus:border-accent focus:outline-none"
                  />
                </div>
                <div>
                  <label className="flex items-center text-sm font-medium mb-1">
                    <Flag className="w-4 h-4 mr-1" />
                    Country
                  </label>
                  <CountrySelector
                    selectedCountry={formData.country}
                    onChange={(countryCode) => setFormData({...formData, country: countryCode})}
                    className="w-full"
                  />
                </div>
                <div className="flex space-x-3">
                  <button
                    onClick={handleEditToggle}
                    className="flex-1 bg-accent hover:bg-yellow-500 text-primary font-medium py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
                  >
                    <Save className="w-4 h-4" />
                    <span>Save Changes</span>
                  </button>
                  <button
                    onClick={() => setEditingProfile(false)}
                    className="flex-1 bg-secondary hover:bg-secondary/80 text-textMuted font-medium py-2 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex items-center space-x-4 mt-2 justify-center md:justify-start">
                <span className="bg-accent text-primary px-3 py-1 rounded-full text-sm font-medium">
                  Level {userData.level}
                </span>
                <span className="text-textMuted text-sm flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Joined {new Date(userData.createdAt).toLocaleDateString()}
                </span>
                <span className="flex items-center text-sm font-medium" title={formData.country === GLOBAL_IDENTIFIER ? 'Global' : `From ${formData.country}`}>
                  <Flag className="w-4 h-4 mr-1 text-textMuted" />
                  <FlagIcon countryCode={formData.country} size="md" />
                </span>
              </div>
            )}
          </div>
        </div>
        
        {!editingProfile && (
          <>
            {/* Bio Section */}
            <div className="mt-4 pt-4 border-t border-border/30">
              <p className="text-text text-sm">{formData.bio}</p>              <div className="flex space-x-4 mt-3 text-sm text-textMuted">
                <button className="hover:text-accent transition-colors flex items-center">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  {formData.github}
                </button>
                <button className="hover:text-accent transition-colors flex items-center">
                  <ExternalLink className="w-4 h-4 mr-1" />
                  {formData.twitter}
                </button>
              </div>
            </div>
            
            {/* XP Progress */}
            <div className="mt-6">
              <div className="flex justify-between text-sm mb-2">
                <span>Level {userData.level}</span>
                <span>Level {userData.level + 1}</span>
              </div>
              <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                <div 
                  className="bg-accent h-3 rounded-full transition-all duration-500"
                  style={{ width: `${xpProgress.percentage}%` }}
                ></div>
              </div>
              <div className="text-center text-sm text-textMuted mt-2">
                {xpProgress.progressXp} / {xpProgress.requiredXp} XP
              </div>
            </div>
          </>
        )}
      </motion.div>

      {/* Quick Stats */}
      <motion.div 
        className="grid grid-cols-2 md:grid-cols-4 gap-4"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <div className="bg-cardBg rounded-lg p-4 text-center border border-border/30 shadow-md hover:shadow-lg transition-shadow">
          <Zap className="w-8 h-8 mx-auto mb-2 text-accent" />
          <div className="text-2xl font-bold text-accent">{userData.stats.avgWpm}</div>
          <div className="text-sm text-textMuted">Avg WPM</div>
        </div>
        <div className="bg-cardBg rounded-lg p-4 text-center border border-border/30 shadow-md hover:shadow-lg transition-shadow">
          <Target className="w-8 h-8 mx-auto mb-2 text-green-500" />
          <div className="text-2xl font-bold text-green-500">{userData.stats.avgAccuracy.toFixed(1)}%</div>
          <div className="text-sm text-textMuted">Accuracy</div>
        </div>
        <div className="bg-cardBg rounded-lg p-4 text-center border border-border/30 shadow-md hover:shadow-lg transition-shadow">
          <Trophy className="w-8 h-8 mx-auto mb-2 text-blue-500" />
          <div className="text-2xl font-bold text-blue-500">{userData.stats.battlesWon}</div>
          <div className="text-sm text-textMuted">Battles Won</div>
        </div>
        <div className="bg-cardBg rounded-lg p-4 text-center border border-border/30 shadow-md hover:shadow-lg transition-shadow">
          <Calendar className="w-8 h-8 mx-auto mb-2 text-purple-500" />
          <div className="text-2xl font-bold text-purple-500">{userData.stats.streak}</div>
          <div className="text-sm text-textMuted">Day Streak</div>
        </div>
      </motion.div>

      {/* Recent Activity */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <Activity className="w-5 h-5 mr-2 text-accent" />
          Recent Activity
        </h2>
        <div className="space-y-3">
          {loadingActivity && (
            <div className="text-center py-8 text-textMuted">Loading activity...</div>
          )}
          {!loadingActivity && recentActivity.length === 0 && (
            <div className="text-center py-8 text-textMuted">No recent activity</div>
          )}
          {!loadingActivity && recentActivity.length > 0 && (
            <>
              {recentActivity.map((activity: UserActivity, index: number) => (
            <motion.div 
              key={activity.id}
              className="flex items-center justify-between p-3 bg-secondary rounded-lg border border-border/20 hover:border-accent/30 transition-colors"
              whileHover={{ x: 5 }}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.05 }}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${activity.iconBg}`}>
                  <activity.icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-medium">{activity.title}</div>
                  <div className="text-sm text-textMuted">{activity.time}</div>
                </div>
              </div>
              <div className="text-right">
                {activity.type === 'test' && (
                  <>
                    <div className="font-bold text-accent">{activity.wpm} WPM</div>
                    <div className="text-sm text-textMuted">{activity.accuracy}% ACC</div>
                  </>
                )}
                {activity.type === 'battle' && (
                  <>
                    <div className="font-bold text-green-500">+{activity.xp} XP</div>
                    <div className="text-sm text-textMuted">Won against {activity.opponent}</div>
                  </>
                )}
                {activity.type === 'achievement' && (
                  <div className="text-sm text-purple-500 font-medium">{activity.description}</div>
                )}
              </div>
            </motion.div>
          ))}
            </>
          )}
        </div>
        <button className="w-full mt-4 text-sm text-textMuted hover:text-accent py-2 transition-colors flex items-center justify-center">
          <span>View All Activity</span>
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </motion.div>
    </div>
  );

  const StatsTab = () => ( // NOSONAR
    <div className="space-y-6">
      {/* Summary Stats Card */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <BarChart3 className="w-5 h-5 mr-2 text-accent" />
          Typing Performance Summary
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-secondary p-3 rounded-lg border border-border/20">
            <div className="text-sm text-textMuted mb-1">Tests Completed</div>
            <div className="text-xl font-bold">{userData.stats.totalTests}</div>
          </div>
          <div className="bg-secondary p-3 rounded-lg border border-border/20">
            <div className="text-sm text-textMuted mb-1">Time Typing</div>
            <div className="text-xl font-bold">{Math.round(userData.stats.totalTimeTyping / 60)} min</div>
          </div>
          <div className="bg-secondary p-3 rounded-lg border border-border/20">
            <div className="text-sm text-textMuted mb-1">Best Streak</div>
            <div className="text-xl font-bold">{userData.stats.bestStreak} days</div>
          </div>
          <div className="bg-secondary p-3 rounded-lg border border-border/20">
            <div className="text-sm text-textMuted mb-1">Current Level</div>
            <div className="text-xl font-bold">{userData.level}</div>
          </div>
        </div>
      </motion.div>

      {/* Detailed Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <motion.div 
          className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Zap className="w-5 h-5 mr-2 text-accent" />
            Typing Performance
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Average WPM</span>
              <span className="font-medium">{userData.stats.avgWpm}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Best WPM</span>
              <span className="font-medium text-accent">{userData.stats.bestWpm}</span>
            </div>
            <div className="w-full bg-secondary/50 h-2 rounded-full mb-4">
              <div 
                className="bg-accent h-2 rounded-full" 
                style={{ width: `${(userData.stats.avgWpm / userData.stats.bestWpm) * 100}%` }}
              ></div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Average Accuracy</span>
              <span className="font-medium">{userData.stats.avgAccuracy.toFixed(1)}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Best Accuracy</span>
              <span className="font-medium text-green-500">{userData.stats.bestAccuracy.toFixed(1)}%</span>
            </div>
            <div className="w-full bg-secondary/50 h-2 rounded-full">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ width: `${(userData.stats.avgAccuracy / userData.stats.bestAccuracy) * 100}%` }}
              ></div>
            </div>
          </div>
        </motion.div>

        <motion.div 
          className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.2 }}
        >
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <Trophy className="w-5 h-5 mr-2 text-blue-500" />
            Battle Stats
          </h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Battles Won</span>
              <span className="font-medium text-green-500">{userData.stats.battlesWon}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Battles Lost</span>
              <span className="font-medium text-red-500">{userData.stats.battlesLost}</span>
            </div>
            <div className="w-full bg-secondary/50 h-2 rounded-full mb-4">
              <div 
                className="bg-blue-500 h-2 rounded-full" 
                style={{ 
                  width: `${(userData.stats.battlesWon / Math.max(1, userData.stats.battlesWon + userData.stats.battlesLost)) * 100}%` 
                }}
              ></div>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Win Rate</span>
              <span className="font-medium">
                {((userData.stats.battlesWon / (userData.stats.battlesWon + userData.stats.battlesLost)) * 100).toFixed(1)}%
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-textMuted">Total Battles</span>
              <span className="font-medium">{userData.stats.battlesWon + userData.stats.battlesLost}</span>
            </div>
            <div className="text-center text-sm text-textMuted border-t border-border/20 pt-3 mt-3">
              {userData.stats.battlesWon > userData.stats.battlesLost ? (
                <span className="text-green-500">You win more battles than you lose!</span>
              ) : (
                <span>Keep practicing to improve your win rate</span>
              )}
            </div>
          </div>
        </motion.div>
      </div>

      {/* Progress Chart Placeholder */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.3 }}
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <TrendingUp className="w-5 h-5 mr-2 text-accent" />
          Progress Over Time
        </h3>
        <div className="h-64 bg-secondary rounded-lg flex items-center justify-center border border-border/20">
          <div className="text-center text-textMuted">
            <TrendingUp className="w-12 h-12 mx-auto mb-2" />
            <p>Progress chart coming soon</p>
            <p className="text-sm">Track your WPM and accuracy improvements</p>
          </div>
        </div>
      </motion.div>
    </div>
  );

  const AchievementsTab = () => { // NOSONAR
    const stats = userState.user?.stats;
    const totalTests = stats?.totalTests ?? 0;
    const bestWpm    = stats?.bestWpm    ?? 0;
    const bestAcc    = stats?.bestAccuracy ?? 0;
    const battlesWon = stats?.battlesWon ?? 0;
    const userLevel  = userState.user?.level ?? 1;

    const mockAchievements = [
      // Beginner
      { id: '1',  name: 'First Steps',           description: 'Complete your first typing test',              icon: Target,         tier: 'beginner',     rarity: 'common',    xp: 50,   unlocked: totalTests >= 1,   progress: Math.min((totalTests / 1) * 100, 100),   date: '2023-09-15' },
      { id: '2',  name: 'Quick Learner',          description: 'Complete 10 typing tests',                    icon: BookOpen,       tier: 'beginner',     rarity: 'common',    xp: 100,  unlocked: totalTests >= 10,  progress: Math.min((totalTests / 10) * 100, 100),  date: '2023-09-20' },
      { id: '3',  name: 'Practice Makes Perfect', description: 'Spend 1 hour typing (60 tests)',              icon: Clock,          tier: 'beginner',     rarity: 'common',    xp: 100,  unlocked: totalTests >= 60,  progress: Math.min((totalTests / 60) * 100, 100),  date: '' },
      { id: '4',  name: 'Rising Star',            description: 'Reach level 5',                             icon: Star,           tier: 'beginner',     rarity: 'common',    xp: 150,  unlocked: userLevel >= 5,    progress: Math.min((userLevel / 5) * 100, 100),    date: '2023-09-30' },
      { id: '21', name: 'First Battle',           description: 'Win your first typing battle',               icon: Swords,         tier: 'beginner',     rarity: 'common',    xp: 75,   unlocked: battlesWon >= 1,   progress: Math.min((battlesWon / 1) * 100, 100),   date: '2023-10-01' },
      // Intermediate
      { id: '5',  name: 'Speed Demon',            description: 'Reach 100 WPM',                             icon: Zap,            tier: 'intermediate', rarity: 'rare',      xp: 200,  unlocked: bestWpm >= 100,    progress: Math.min((bestWpm / 100) * 100, 100),    date: '2023-10-02' },
      { id: '6',  name: 'Accuracy Master',        description: 'Achieve 99% accuracy',                      icon: CheckCircle2,   tier: 'intermediate', rarity: 'rare',      xp: 200,  unlocked: bestAcc >= 99,     progress: Math.min((bestAcc / 99) * 100, 100),     date: '' },
      { id: '7',  name: 'Code Warrior',           description: 'Complete 50 code typing tests',             icon: Code2,          tier: 'intermediate', rarity: 'rare',      xp: 250,  unlocked: totalTests >= 50,  progress: Math.min((totalTests / 50) * 100, 100),  date: '' },
      { id: '8',  name: 'Consistent Typer',       description: 'Complete 25 tests in a row',                icon: Calendar,       tier: 'intermediate', rarity: 'rare',      xp: 300,  unlocked: totalTests >= 25,  progress: Math.min((totalTests / 25) * 100, 100),  date: '' },
      { id: '9',  name: 'Typing Veteran',         description: 'Complete 100 typing tests',                 icon: Medal,          tier: 'intermediate', rarity: 'rare',      xp: 350,  unlocked: totalTests >= 100, progress: Math.min((totalTests / 100) * 100, 100), date: '' },
      { id: '22', name: 'Keyboard Warrior',       description: 'Reach level 10',                            icon: Keyboard,       tier: 'intermediate', rarity: 'rare',      xp: 300,  unlocked: userLevel >= 10,   progress: Math.min((userLevel / 10) * 100, 100),   date: '' },
      // Expert
      { id: '10', name: 'Battle Champion',        description: 'Win 25 battles',                            icon: Trophy,         tier: 'expert',       rarity: 'epic',      xp: 400,  unlocked: battlesWon >= 25,  progress: Math.min((battlesWon / 25) * 100, 100),  date: '' },
      { id: '11', name: 'Speed Milestone',        description: 'Reach 120 WPM',                             icon: Flame,          tier: 'expert',       rarity: 'epic',      xp: 500,  unlocked: bestWpm >= 120,    progress: Math.min((bestWpm / 120) * 100, 100),    date: '' },
      { id: '12', name: 'Python Prodigy',         description: 'Complete 200 tests',                        icon: Cpu,            tier: 'expert',       rarity: 'epic',      xp: 450,  unlocked: totalTests >= 200, progress: Math.min((totalTests / 200) * 100, 100), date: '' },
      { id: '13', name: 'JavaScript Genius',      description: 'Reach level 20',                            icon: Code2,          tier: 'expert',       rarity: 'epic',      xp: 450,  unlocked: userLevel >= 20,   progress: Math.min((userLevel / 20) * 100, 100),   date: '' },
      { id: '14', name: 'Language Explorer',      description: 'Complete 500 tests',                        icon: BadgeCheck,     tier: 'expert',       rarity: 'epic',      xp: 500,  unlocked: totalTests >= 500, progress: Math.min((totalTests / 500) * 100, 100), date: '' },
      { id: '23', name: 'Rapid Fire',             description: 'Reach 130 WPM with 95%+ accuracy',          icon: Timer,          tier: 'expert',       rarity: 'epic',      xp: 450,  unlocked: bestWpm >= 130 && bestAcc >= 95, progress: Math.min(((bestWpm + bestAcc) / (130 + 95)) * 100, 100), date: '' },
      // Master
      { id: '15', name: 'Keyboard Legend',        description: 'Reach level 50',                            icon: Clock,          tier: 'master',       rarity: 'legendary', xp: 1000, unlocked: userLevel >= 50,   progress: Math.min((userLevel / 50) * 100, 100),   date: '' },
      { id: '16', name: 'Elite Performer',        description: 'Reach 150 WPM',                             icon: Rocket,         tier: 'master',       rarity: 'legendary', xp: 1500, unlocked: bestWpm >= 150,    progress: Math.min((bestWpm / 150) * 100, 100),    date: '' },
      { id: '17', name: 'Dedication Master',      description: 'Complete 1,000 tests',                      icon: Shield,         tier: 'master',       rarity: 'legendary', xp: 2000, unlocked: totalTests >= 1000, progress: Math.min((totalTests / 1000) * 100, 100), date: '' },
      { id: '18', name: 'Code Virtuoso',          description: 'Reach 180 WPM',                             icon: Lightbulb,      tier: 'master',       rarity: 'legendary', xp: 1800, unlocked: bestWpm >= 180,    progress: Math.min((bestWpm / 180) * 100, 100),    date: '' },
      { id: '19', name: 'Battle Royale',          description: 'Win 100 battles',                           icon: Gem,            tier: 'master',       rarity: 'legendary', xp: 2500, unlocked: battlesWon >= 100, progress: Math.min((battlesWon / 100) * 100, 100), date: '' },
      { id: '20', name: 'TypeRush Master',        description: 'Unlock all other achievements',             icon: Award,          tier: 'master',       rarity: 'legendary', xp: 5000, unlocked: false,             progress: 0, date: '' },
    ];

    const rarityStyles: Record<string, { border: string; bg: string; text: string; glow: string; badge: string }> = {
      common:    { border: 'border-gray-500/60',   bg: 'bg-gray-500/10',   text: 'text-gray-300',   glow: '',                                           badge: 'bg-gray-600/40 text-gray-300' },
      rare:      { border: 'border-blue-400/70',   bg: 'bg-blue-500/10',   text: 'text-blue-300',   glow: '',                                           badge: 'bg-blue-500/30 text-blue-300' },
      epic:      { border: 'border-purple-400/70', bg: 'bg-purple-500/10', text: 'text-purple-300', glow: '',                                           badge: 'bg-purple-500/30 text-purple-300' },
      legendary: { border: 'border-yellow-400/80', bg: 'bg-yellow-400/10', text: 'text-yellow-300', glow: 'shadow-[0_0_18px_2px_rgba(250,204,21,0.25)]', badge: 'bg-yellow-400/25 text-yellow-300' },
    };

    const tierMeta: Record<string, { label: string; icon: React.ReactNode; barColor: string; gradFrom: string }> = {
      beginner:     { label: 'Beginner',     icon: <CircleDashed className="w-5 h-5 text-gray-400" />,  barColor: 'bg-gray-400',    gradFrom: 'from-gray-700/30' },
      intermediate: { label: 'Intermediate', icon: <BadgeCheck   className="w-5 h-5 text-blue-400" />, barColor: 'bg-blue-400',    gradFrom: 'from-blue-700/30' },
      expert:       { label: 'Expert',       icon: <Sparkles     className="w-5 h-5 text-purple-400" />, barColor: 'bg-purple-400', gradFrom: 'from-purple-700/30' },
      master:       { label: 'Master',       icon: <Crown        className="w-5 h-5 text-yellow-400" />, barColor: 'bg-gradient-to-r from-yellow-500 to-amber-400', gradFrom: 'from-yellow-700/30' },
    };

    const earnedXP = mockAchievements.filter(a => a.unlocked).reduce((s, a) => s + a.xp, 0);
    const totalXP  = mockAchievements.reduce((s, a) => s + a.xp, 0);
    const unlockedCount = mockAchievements.filter(a => a.unlocked).length;

    const byTier = (t: string) => mockAchievements.filter(a => a.tier === t);

    const [filterRarity, setFilterRarity] = React.useState<string>('all');
    const rarityFilters = ['all', 'common', 'rare', 'epic', 'legendary'];

    const getFilterButtonClass = (r: string, active: boolean): string => {
      if (!active) return 'bg-secondary/50 text-textMuted border-border/30 hover:border-border';
      if (r === 'all')    return 'bg-accent text-primary border-accent';
      if (r === 'common') return 'bg-gray-500/30 text-gray-200 border-gray-400';
      if (r === 'rare')   return 'bg-blue-500/30 text-blue-200 border-blue-400';
      if (r === 'epic')   return 'bg-purple-500/30 text-purple-200 border-purple-400';
      return 'bg-yellow-400/20 text-yellow-200 border-yellow-400';
    };

    return (
      <div className="space-y-6">
        {/* Summary Banner */}
        <motion.div
          className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30 relative overflow-hidden"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        >
          {/* decorative shimmer strip */}
          <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-transparent via-accent to-transparent opacity-60" />

          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-bold flex items-center gap-2">
              <Award className="w-5 h-5 text-accent" />
              Achievements
            </h3>
            <div className="flex items-center gap-2 bg-accent/15 border border-accent/30 px-3 py-1.5 rounded-full">
              <Star className="w-4 h-4 text-accent" />
              <span className="text-accent font-bold text-sm">{earnedXP.toLocaleString()} / {totalXP.toLocaleString()} XP</span>
            </div>
          </div>

          {/* Overall bar */}
          <div className="mb-5">
            <div className="flex justify-between text-xs text-textMuted mb-1.5">
              <span>{unlockedCount} of {mockAchievements.length} unlocked</span>
              <span>{Math.round((unlockedCount / mockAchievements.length) * 100)}%</span>
            </div>
            <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-accent to-yellow-400 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${(unlockedCount / mockAchievements.length) * 100}%` }}
                transition={{ duration: 0.8, ease: 'easeOut' }}
              />
            </div>
          </div>

          {/* Tier mini cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(['beginner', 'intermediate', 'expert', 'master'] as const).map(tier => {
              const list = byTier(tier);
              const done = list.filter(a => a.unlocked).length;
              const meta = tierMeta[tier];
              return (
                <div key={tier} className="bg-secondary/40 rounded-lg p-3 border border-border/20">
                  <div className="flex items-center gap-1.5 mb-2">{meta.icon}<span className="text-xs font-semibold">{meta.label}</span></div>
                  <div className="text-lg font-bold">{done}<span className="text-textMuted text-sm font-normal">/{list.length}</span></div>
                  <div className="h-1 bg-secondary rounded-full mt-2 overflow-hidden">
                    <motion.div
                      className={`h-full rounded-full ${meta.barColor}`}
                      initial={{ width: 0 }}
                      animate={{ width: `${(done / list.length) * 100}%` }}
                      transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* â”€â”€ Rarity Filter â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        <div className="flex items-center gap-2 flex-wrap">
          {rarityFilters.map(r => (
            <button
              key={r}
              onClick={() => setFilterRarity(r)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${getFilterButtonClass(r, filterRarity === r)}`}
            >
              {r.charAt(0).toUpperCase() + r.slice(1)}
            </button>
          ))}
        </div>

        {/* â”€â”€ Tier Sections â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
        {(['beginner', 'intermediate', 'expert', 'master'] as const).map((tier, idx) => {
          const list = byTier(tier).filter(a => filterRarity === 'all' || a.rarity === filterRarity);
          if (list.length === 0) return null;
          const meta = tierMeta[tier];
          return (
            <motion.div
              key={tier}
              className={`bg-cardBg rounded-xl p-6 shadow-lg border border-border/30 relative overflow-hidden`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.08 * idx }}
            >
              {/* tier background glow */}
              <div className={`absolute top-0 right-0 w-40 h-40 bg-gradient-to-bl ${meta.gradFrom} to-transparent opacity-40 rounded-bl-full pointer-events-none`} />

              <div className="flex items-center justify-between mb-5 relative z-10">
                <h4 className="font-bold flex items-center gap-2">{meta.icon}{meta.label} Tier</h4>
                <div className="flex items-center gap-1 text-xs text-accent/80">
                  <ChevronsUp className="w-4 h-4" />
                  {byTier(tier).filter(a => a.unlocked).reduce((s, a) => s + a.xp, 0).toLocaleString()} XP earned
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 relative z-10">
                {list.map((ach, aIdx) => {
                  const rs = rarityStyles[ach.rarity];
                  const IconComp = ach.icon;
                  return (
                    <motion.div
                      key={ach.id}
                      className={`rounded-xl border p-4 flex gap-3 relative overflow-hidden transition-all
                        ${ach.unlocked ? `${rs.border} ${rs.bg} ${rs.glow}` : 'border-border/20 bg-secondary/30'}
                      `}
                      initial={{ opacity: 0, scale: 0.96 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.25, delay: aIdx * 0.04 }}
                      whileHover={{ scale: 1.02 }}
                    >
                      {/* Legendary shimmer */}
                      {ach.rarity === 'legendary' && ach.unlocked && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-400/8 to-transparent pointer-events-none" />
                      )}

                      {/* Progress fill (bottom bar) */}
                      {!ach.unlocked && ach.progress > 0 && (
                        <motion.div
                          className={`absolute bottom-0 left-0 h-0.5 ${meta.barColor}`}
                          initial={{ width: 0 }}
                          animate={{ width: `${ach.progress}%` }}
                          transition={{ duration: 0.6, ease: 'easeOut', delay: aIdx * 0.04 + 0.3 }}
                        />
                      )}

                      {/* Icon */}
                      <div className={`flex-shrink-0 w-11 h-11 rounded-xl flex items-center justify-center border ${
                        ach.unlocked ? `${rs.border} ${rs.bg}` : 'border-border/20 bg-secondary/60'
                      }`}>
                        {ach.unlocked
                          ? <IconComp className={`w-5 h-5 ${rs.text}`} />
                          : <Lock className="w-4 h-4 text-textMuted/40" />}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-0.5">
                          <span className={`font-semibold text-sm truncate ${ach.unlocked ? rs.text : 'text-textMuted'}`}>
                            {ach.name}
                          </span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium flex-shrink-0 ${rs.badge}`}>
                            +{ach.xp} XP
                          </span>
                        </div>

                        <p className="text-xs text-textMuted leading-relaxed">{ach.description}</p>

                        <div className="flex items-center justify-between mt-2">
                          {ach.unlocked ? (
                            <div className="flex items-center gap-1 text-xs text-green-400">
                              <CheckCheck className="w-3 h-3" />
                              <span>Unlocked{ach.date ? ` Â· ${new Date(ach.date).toLocaleDateString()}` : ''}</span>
                            </div>
                          ) : (
                            <div className="text-xs text-textMuted/70">
                              {ach.progress > 0 ? `${Math.round(ach.progress)}% complete` : 'Locked'}
                            </div>
                          )}
                          <span className={`text-xs font-medium capitalize ${rs.text}`}>{ach.rarity}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>
    );
  };


  const SettingsTab = () => ( // NOSONAR
    <div className="space-y-6">
      {/* Themes Section */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <PenTool className="w-5 h-5 mr-2 text-accent" />
          Typing Preferences
        </h3>
        <div className="space-y-4">
          <div>
            <span className="block text-sm font-medium mb-2">Theme</span>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {/* Default theme - always available */}
              <button 
                onClick={() => {
                  updatePreferences({ theme: 'default' });
                  toast.success('Default theme applied!');
                }}
                className={`w-full bg-secondary border-2 ${userData.preferences.theme === 'default' ? 'border-accent' : 'border-transparent'} rounded-lg p-3 text-center text-accent font-medium hover:border-accent/70 transition-colors`}
              >
                Default
              </button>
              
              {/* Matrix theme - premium only */}
              <div className="relative">
                <button 
                  onClick={() => {
                    if (userData.isPremium) {
                      updatePreferences({ theme: 'matrix' });
                      toast.success('Matrix theme applied!');
                    } else {
                      toast.error('Matrix theme requires Premium subscription');
                    }
                  }}
                  className={`w-full bg-secondary border-2 ${userData.preferences.theme === 'matrix' ? 'border-green-400' : 'border-transparent'} rounded-lg p-3 text-center ${
                    userData.isPremium ? 'text-green-400 hover:border-green-400/70' : 'text-textMuted opacity-60'
                  } transition-colors`}
                  disabled={!userData.isPremium}
                >
                  Matrix
                </button>
                {!userData.isPremium && (
                  <div className="absolute -top-2 -right-2 bg-accent text-primary rounded-full p-1">
                    <Crown className="w-3 h-3" />
                  </div>
                )}
              </div>
              
              {/* Neon theme - premium only */}
              <div className="relative">
                <button 
                  onClick={() => {
                    if (userData.isPremium) {
                      updatePreferences({ theme: 'neon' });
                      toast.success('Neon theme applied!');
                    } else {
                      toast.error('Neon theme requires Premium subscription');
                    }
                  }}
                  className={`w-full bg-secondary border-2 ${userData.preferences.theme === 'neon' ? 'border-purple-400' : 'border-transparent'} rounded-lg p-3 text-center ${
                    userData.isPremium ? 'text-purple-400 hover:border-purple-400/70' : 'text-textMuted opacity-60'
                  } transition-colors`}
                  disabled={!userData.isPremium}
                >
                  Neon
                </button>
                {!userData.isPremium && (
                  <div className="absolute -top-2 -right-2 bg-accent text-primary rounded-full p-1">
                    <Crown className="w-3 h-3" />
                  </div>
                )}
              </div>
              
              {/* Ember theme - lifetime premium exclusive */}
              <div className="relative">
                <button 
                  onClick={() => {
                    if (userData.isPremium === 'lifetime') {
                      updatePreferences({ theme: 'ember' });
                      toast.success('Exclusive Ember theme applied!');
                    } else {
                      toast.error('Ember theme requires Lifetime Premium subscription');
                    }
                  }}
                  className={`w-full bg-secondary border-2 ${userData.preferences.theme === 'ember' ? 'border-red-400' : 'border-transparent'} rounded-lg p-3 text-center ${
                    userData.isPremium === 'lifetime' ? 'text-red-400 hover:border-red-400/70' : 'text-textMuted opacity-60'
                  } transition-colors`}
                  disabled={userData.isPremium !== 'lifetime'}
                >
                  Ember
                </button>
                {userData.isPremium !== 'lifetime' && (
                  <div className="absolute -top-2 -right-2 bg-purple-500 text-white rounded-full p-1" title="Lifetime Premium Exclusive">
                    <Gift className="w-3 h-3" />
                  </div>
                )}
              </div>
            </div>
            {!userData.isPremium && (
              <div className="mt-2 text-xs text-textMuted flex items-center">
                <Lock className="w-3 h-3 mr-1" />
                <span>Unlock premium themes with a Premium subscription</span>
              </div>
            )}
            {userData.isPremium && userData.isPremium !== 'lifetime' && (
              <div className="mt-2 text-xs text-textMuted flex items-center">
                <Gift className="w-3 h-3 mr-1" />
                <span>Upgrade to Lifetime Premium to unlock the exclusive Ember theme</span>
              </div>
            )}
          </div>
          <div>
            <label htmlFor="font-select" className="block text-sm font-medium mb-2">Font</label>
            <select id="font-select" className="w-full bg-secondary border border-border/20 rounded-lg px-3 py-2 text-text focus:border-accent focus:outline-none">
              <option value="mono">Monospace</option>
              <option value="hacker">Hacker</option>
              <option value="pixel">Pixel</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Font Size: {userData.preferences.fontSize}px</label>
            <input
              type="range"
              min="14"
              max="24"
              defaultValue={userData.preferences.fontSize}
              className="w-full accent-accent"
            />
            <div className="flex justify-between text-xs text-textMuted mt-1">
              <span>14px</span>
              <span>24px</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Sound and Typing Settings */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.1 }}
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <Settings className="w-5 h-5 mr-2 text-accent" />
          Preferences
        </h3>
        <div className="space-y-4">
          {/* Sound Settings */}
          <div>
            <h3 className="text-md font-medium mb-3">Sound</h3>
            <div className="flex items-center justify-between">
              <span>Keyboard Sounds</span>
              <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle keyboard sounds">
                <input 
                  type="checkbox" 
                  className="sr-only peer"
                  defaultChecked={userData.preferences.soundEnabled}
                  onChange={(e) => updatePreferences({ soundEnabled: e.target.checked })}
                />
                <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
              </label>
            </div>
          </div>
          
          {/* Typing Settings */}
          <div>
            <h3 className="text-md font-medium mb-3">Typing Settings</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Auto-correct</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle auto-correct">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <span>Highlight errors</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle highlight errors">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <span>Smooth caret</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle smooth caret">
                  <input 
                    type="checkbox"
                    className="sr-only peer" 
                    defaultChecked={userData.preferences.smoothCaret}
                    onChange={(e) => updatePreferences({ smoothCaret: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <span>Blind mode</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle blind mode">
                  <input 
                    type="checkbox" 
                    className="sr-only peer"
                    defaultChecked={userData.preferences.blindMode}
                    onChange={(e) => updatePreferences({ blindMode: e.target.checked })}
                  />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Notifications and Privacy */}
      <motion.div 
        className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.2 }}
      >
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <UserIcon className="w-5 h-5 mr-2 text-accent" />
          Account Settings
        </h3>
        <div className="space-y-4">
          {/* Notifications */}
          <div>
            <h3 className="text-md font-medium mb-3">Notifications</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Email notifications</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle email notifications">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <span>New challenges</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle new challenges notifications">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
            </div>
          </div>
          
          {/* Privacy */}
          <div>
            <h3 className="text-md font-medium mb-3">Privacy</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span>Show my name on leaderboards</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle show name on leaderboards">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
              <div className="flex items-center justify-between">
                <span>Share my results publicly</span>
                <label className="relative inline-flex items-center cursor-pointer" aria-label="Toggle share results publicly">
                  <input type="checkbox" className="sr-only peer" defaultChecked />
                  <div className="w-11 h-6 bg-secondary rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                </label>
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between p-3 bg-secondary rounded-lg border border-border/20">
            <div>
              <div className="font-medium">Email Notifications</div>
              <div className="text-sm text-textMuted">Receive updates about your progress</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" aria-label="Email Notifications toggle">
              <input type="checkbox" value="" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-3 bg-secondary rounded-lg border border-border/20">
            <div>
              <div className="font-medium">Public Profile</div>
              <div className="text-sm text-textMuted">Let others see your stats</div>
            </div>
            <label className="relative inline-flex items-center cursor-pointer" aria-label="Public Profile toggle">
              <input type="checkbox" value="" className="sr-only peer" defaultChecked />
              <div className="w-11 h-6 bg-secondary peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-accent rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
            </label>
          </div>

          <button 
            onClick={() => toast.success('Password reset email sent!')}
            className="w-full bg-secondary hover:bg-secondary/80 text-text font-medium py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Lock className="w-4 h-4" />
            <span>Change Password</span>
          </button>
          
          <button 
            onClick={() => toast.success('Redirecting to premium upgrade...')}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
          >
            <Crown className="w-4 h-4" />
            <span>Upgrade to Premium</span>
          </button>
          
          {/* Danger Zone */}
          <div>
            <h3 className="text-lg font-medium text-red-500 mb-3">Danger Zone</h3>
            <button 
              onClick={() => toast.error('Account deletion cancelled')}
              className="w-full bg-red-600 hover:bg-red-700 text-white font-medium py-2 rounded-lg transition-colors flex items-center justify-center space-x-2"
            >
              <Trash2 className="w-4 h-4" />
              <span>Delete Account</span>
            </button>
          </div>
        </div>
      </motion.div>

      <button 
        onClick={() => toast.success('All settings saved!')}
        className="w-full bg-accent hover:bg-yellow-500 text-primary font-medium py-3 rounded-lg transition-colors mb-4"
      >
        Save All Settings
      </button>

      {/* Logout Button */}
      <button 
        onClick={() => {
          logout();
          authLogout();
          toast.success('Logged out successfully!');
          navigate('/');
        }}
        className="w-full bg-orange-600/10 hover:bg-orange-600/20 border border-orange-600/30 text-orange-500 font-medium py-3 rounded-lg transition-colors flex items-center justify-center space-x-2"
      >
        <Lock className="w-4 h-4" />
        <span>Logout</span>
      </button>
    </div>
  );

  const PublicProfileTab = () => { // NOSONAR
    // Format time function
    const formatTime = (seconds: number) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      
      if (hours > 0) {
        return `${hours}h ${minutes}m`;
      }
      return `${minutes}m`;
    };
    
    // Format date
    const formatDate = (dateString: string) => {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };
    
    // Get premium badge based on subscription type
    const getPremiumBadge = () => {
      if (!userData.isPremium) return null;
      
      let badgeText = 'Premium';
      let badgeColor = 'bg-accent';
      
      if (userData.isPremium === 'lifetime') {
        badgeText = 'Lifetime Pro';
        badgeColor = 'bg-gradient-to-r from-purple-500 to-accent';
      } else if (userData.isPremium === 'yearly') {
        badgeText = 'Pro (Yearly)';
      } else if (userData.isPremium === 'monthly') {
        badgeText = 'Pro (Monthly)';
      }
      
      return (
        <div className={`${badgeColor} text-primary text-xs font-medium px-2 py-0.5 rounded-full flex items-center`}>
          <Crown className="w-3 h-3 mr-1" />
          {badgeText}
        </div>
      );
    };
    
    return (
      <div className="space-y-6">
        {/* Loading state */}
        {isLoadingUser && (
          <div className="py-12 text-center">
            <div className="animate-spin w-8 h-8 border-2 border-accent border-t-transparent rounded-full mx-auto mb-4"></div>
            <p className="text-textMuted">Loading user profile...</p>
          </div>
        )}
      
        {!isLoadingUser && (
          <>
            {/* Public Profile Header */}
            <motion.div 
              className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="flex flex-col md:flex-row md:items-center md:space-x-6">
                <div className="w-20 h-20 bg-accent/20 rounded-full flex items-center justify-center mb-4 md:mb-0 mx-auto md:mx-0">
                  <UserIcon className="w-10 h-10 text-accent" />
                </div>
                
                <div className="flex-1 text-center md:text-left">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <div className="flex items-center justify-center md:justify-start space-x-2 mb-1">
                        <h1 className="text-2xl font-bold">{userData.username}</h1>
                        <FlagIcon countryCode={userData.country || 'US'} size="md" />
                      </div>
                      
                      <div className="flex items-center justify-center md:justify-start space-x-2">
                        {getPremiumBadge()}
                        <div className="text-xs text-textMuted">
                          Member since {formatDate(userData.createdAt)}
                        </div>
                      </div>
                    </div>
                    
                    <div className="mt-3 md:mt-0 flex items-center justify-center md:justify-end space-x-3">
                      <div className="text-center">
                        <div className="text-lg font-bold text-accent">{userData.level}</div>
                        <div className="text-xs text-textMuted">Level</div>
                      </div>
                      
                      <div className="h-10 border-r border-border/30"></div>
                      
                      <div className="text-center">
                        <div className="text-lg font-bold text-accent">{userData.totalXp?.toLocaleString() || 0}</div>
                        <div className="text-xs text-textMuted">XP</div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Social Links */}
                  <div className="mt-4 flex items-center justify-center md:justify-start space-x-4">
                    {formData.github && (
                      <a 
                        href={`https://${formData.github.replace(/^https?:\/\//, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-textMuted hover:text-accent flex items-center transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 mr-1" />
                        <span className="text-sm">{formData.github.replace(/^https?:\/\/(www\.)?/, '')}</span>
                      </a>
                    )}
                    
                    {formData.twitter && (
                      <a 
                        href={`https://twitter.com/${formData.twitter.replace('@', '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-textMuted hover:text-accent flex items-center transition-colors"
                      >
                        <ExternalLink className="w-5 h-5 mr-1" />
                        <span className="text-sm">{formData.twitter}</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
           
            </motion.div>
            
            {/* Typing Stats */}
            <motion.div 
              className="bg-cardBg rounded-xl p-6 shadow-lg border border-border/30"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <h3 className="text-lg font-semibold mb-5 flex items-center">
                <TrendingUp className="w-5 h-5 mr-2 text-accent" />
                Typing Statistics
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-secondary rounded-lg p-3 border border-border/20">
                  <div className="text-sm text-textMuted mb-1">Best WPM</div>
                  <div className="text-xl font-bold text-accent">{userData.stats?.bestWpm || 0}</div>
                </div>
                <div className="bg-secondary rounded-lg p-3 border border-border/20">
                  <div className="text-sm text-textMuted mb-1">Avg. Accuracy</div>
                  <div className="text-xl font-bold text-green-500">{(userData.stats?.avgAccuracy || 0).toFixed(1)}%</div>
                </div>
                <div className="bg-secondary rounded-lg p-3 border border-border/20">
                  <div className="text-sm text-textMuted mb-1">Total Tests</div>
                  <div className="text-xl font-bold text-blue-500">{userData.stats?.totalTests || 0}</div>
                </div>
                <div className="bg-secondary rounded-lg p-3 border border-border/20">
                  <div className="text-sm text-textMuted mb-1">Time Typing</div>
                  <div className="text-xl font-bold text-purple-500">{formatTime(userData.stats?.totalTimeTyping || 0)}</div>
                </div>
              </div>
            
              <div className="space-y-6">
                {/* Battles Stats */}
                <div>
                  <h4 className="text-md font-semibold mb-3 flex items-center">
                    <Medal className="w-4 h-4 mr-1.5 text-blue-500" />
                    Battles
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20 flex flex-col md:flex-row md:items-center md:space-x-3">
                      <Trophy className="w-5 h-5 text-blue-500 mb-2 md:mb-0" />
                      <div>
                        <div className="text-sm text-textMuted">Battles Won</div>
                        <div className="text-xl font-bold text-blue-500">{userData.stats?.battlesWon || 0}</div>
                      </div>
                    </div>
                    <div className="bg-blue-500/10 rounded-lg p-3 border border-blue-500/20 flex flex-col md:flex-row md:items-center md:space-x-3">
                      <Target className="w-5 h-5 text-blue-500 mb-2 md:mb-0" />
                      <div>
                        <div className="text-sm text-textMuted">Win Rate</div>
                        <div className="text-xl font-bold text-blue-500">
                          {userData.stats?.battlesWon && (userData.stats.battlesWon + userData.stats.battlesLost) > 0
                            ? `${Math.round((userData.stats.battlesWon / (userData.stats.battlesWon + userData.stats.battlesLost)) * 100)}%`
                            : '0%'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Time Mode Stats */}
                <div>
                  <h4 className="text-md font-semibold mb-3 flex items-center">
                    <Timer className="w-4 h-4 mr-1.5 text-accent" />
                    Time Mode - Best WPM
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">15 seconds</div>
                      <div className="text-lg font-bold text-accent">{Math.round((userData.stats?.bestWpm || 0) * 1.15)} wpm</div>
                    </div>
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">30 seconds</div>
                      <div className="text-lg font-bold text-accent">{Math.round((userData.stats?.bestWpm || 0) * 1.05)} wpm</div>
                    </div>
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">60 seconds</div>
                      <div className="text-lg font-bold text-accent">{userData.stats?.bestWpm || 0} wpm</div>
                    </div>
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">120 seconds</div>
                      <div className="text-lg font-bold text-accent">{Math.round((userData.stats?.bestWpm || 0) * 0.95)} wpm</div>
                    </div>
                  </div>
                </div>
                
                {/* Words Mode Stats */}
                <div>
                  <h4 className="text-md font-semibold mb-3 flex items-center">
                    <BookOpen className="w-4 h-4 mr-1.5 text-green-500" />
                    Words Mode - Best WPM
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">10 words</div>
                      <div className="text-lg font-bold text-green-500">{Math.round((userData.stats?.bestWpm || 0) * 1.2)} wpm</div>
                    </div>
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">25 words</div>
                      <div className="text-lg font-bold text-green-500">{Math.round((userData.stats?.bestWpm || 0) * 1.1)} wpm</div>
                    </div>
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">50 words</div>
                      <div className="text-lg font-bold text-green-500">{Math.round(userData.stats?.bestWpm || 0)} wpm</div>
                    </div>
                    <div className="bg-secondary rounded-lg p-3 border border-border/20">
                      <div className="text-sm text-textMuted mb-1">100 words</div>
                      <div className="text-lg font-bold text-green-500">{Math.round((userData.stats?.bestWpm || 0) * 0.9)} wpm</div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
            
            {!isOwnProfile && isAuthenticated && (
              <motion.div 
                className="flex justify-center space-x-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: 0.2 }}
              >
                <button className="bg-accent/10 hover:bg-accent/20 text-accent px-4 py-2 rounded-lg flex items-center transition-colors">
                  <Users className="w-4 h-4 mr-2" />
                  Add Friend
                </button>
                
                <button className="bg-blue-500/10 hover:bg-blue-500/20 text-blue-500 px-4 py-2 rounded-lg flex items-center transition-colors">
                  <Target className="w-4 h-4 mr-2" />
                  Challenge to Battle
                </button>
              </motion.div>
            )}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="max-w-4xl mx-auto px-4 pb-10">
      {/* Tab Navigation */}
      <motion.div 
        className="flex justify-center mb-8 relative"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Back button when viewing another user's profile */}
        {!isOwnProfile && (
          <button 
            onClick={() => navigate(-1)} 
            className="absolute top-2 left-4 flex items-center text-textMuted hover:text-accent"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            <span>Back</span>
          </button>
        )}
        <div className="bg-cardBg rounded-lg p-1 flex shadow-md border border-border/30">
          {tabs
            .filter(tab => isOwnProfile || (tab.id !== 'settings' && tab.id !== 'overview'))
            .map(tab => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center space-x-2 px-4 py-2 rounded-md transition-all ${
                    activeTab === tab.id
                      ? 'bg-accent text-primary font-medium'
                      : 'text-textMuted hover:text-accent'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
        </div>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'overview' && <OverviewTab />}
          {activeTab === 'public-profile' && <PublicProfileTab />}
          {activeTab === 'stats' && <StatsTab />}
          {activeTab === 'achievements' && <AchievementsTab />}
          {activeTab === 'settings' && <SettingsTab />}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default Profile;
